import { GetStaticPropsContext, Redirect } from 'next';
import { createApolloClient, initializeApollo } from './apolloClient';
import { GET_POST } from 'api/posts/queries';
import { ParsedUrlQuery } from 'querystring';
import {
  GetAllPostsSlugsQuery,
  GetAllPostsSlugsQueryVariables,
  PostFragmentFragment,
  PostIdType,
  PostQuery,
  PostQueryVariables,
  TotalPostsQuery,
  TotalPostsQueryVariables,
} from 'types/generated/graphql';
import { GET_ALL_POSTS_SLUGS } from 'api/category/queries';
import { PageParams as PagePageParams } from 'pages/page/[page]';
import { getLocalePath, getWPLocale, logger } from 'utils';
import redirects from '../redirects.json';
import TemporaryCover from '../public/temporary/post-cover-example.png';
import { MappedPost } from 'ui';
import { gql } from '@apollo/client';
import { EventIds } from './EventIds';
import { PreviewDataProps } from './previewApi';
import { CategoryPostsPageParams } from 'pages/category/[category]/page/[page]';

export interface PageParams extends ParsedUrlQuery {
  post: string;
}

const RESERVED_FILE_NAMES_REGEXP =
  /\.css\.map|favicon\.ico|apple-app-site-association|apple-touch-icon-precomposed\.png|apple-touch-icon\.png/;

export const NB_POSTS_PER_PAGE = 18;

export function findRedirectPath(
  slug: string,
  locale: string
): Redirect | null {
  return (
    redirects.find((redirect) => {
      if (redirect.locale === false && redirect.source.includes(':locale')) {
        const source = redirect.source.replace(':locale', locale);
        return source === `/${locale}/${slug}`;
      }

      return redirect.source === `/${slug}`;
    }) ?? null
  );
}

type GetPostData = (
  props: GetStaticPropsContext<PageParams, PreviewDataProps>
) => Promise<(PostFragmentFragment & { id: string }) | null>;

export const getPostData: GetPostData = async ({
  params,
  locale,
  preview: isPreview = false,
  previewData,
}) => {
  // Create new apollo client instead of calling `initializeApolloClient` which returns a shared one.
  // We do that to avoid caching all posts data in apollo cache, which is then sent to client
  const apolloClient = createApolloClient();

  const postPreview = isPreview && previewData ? previewData : null;
  const previewToken =
    isPreview && previewData?.token ? previewData?.token : null;

  const slug = params?.post;
  const skip = RESERVED_FILE_NAMES_REGEXP.test(`${slug}`);
  if (slug && !skip) {
    let { data, errors, error } = await apolloClient.query<
      PostQuery,
      PostQueryVariables
    >({
      query: GET_POST,
      variables: {
        asPreview: isPreview,
        id: isPreview ? `${postPreview?.id}` : slug,
        idType: isPreview ? PostIdType.DatabaseId : PostIdType.Slug,
        lessonPostsPostsFirst: 20,
        lessonPostsPostsWhere: {
          wpmlLanguage: locale,
        },
      },
      errorPolicy: 'all',
      ...(previewToken ? { context: { previewToken } } : {}),
    });

    const wpLocale = getWPLocale(locale);
    const currentPostLocale = data?.post?.locale?.locale;

    let postData = data?.post || null;

    // Throw error only if we don't have any data available
    if (!postData && error) {
      logger.error('Failed to fetch post', EventIds.PostPageError, error);

      throw error;
    }
    if (!postData && errors && errors.length > 0) {
      logger.error('Failed to fetch post', EventIds.PostPageError, errors);
      throw errors[1];
    }

    /**
     * Find by locale because of no way to get filtered post by requested locale
     * and slugs could be the same in different translations
     */
    if (postData && wpLocale !== currentPostLocale) {
      const postTranslated = postData.translated?.find(
        (item) => item?.locale?.locale === wpLocale
      );
      if (postTranslated) {
        return { ...postTranslated, id: '' };
      }
    }

    // cleanup translation as we don't need it
    if (postData?.translated) {
      postData = {
        ...postData,
        translated: null,
      };
    }

    return postData;
  }
  return null;
};

export type GetPageDataReturnData = Awaited<ReturnType<typeof getPageData>>;

export type RestApiPost = {
  id: number;
  format: 'standard' | 'video';
  slug: string;
  title: {
    rendered: string;
  };
  ACF: {
    accent_color: string;
    video_id: string;
  };
  _embedded: {
    'wp:featuredmedia'?: {
      id: number;
      source_url: string;
    }[];
    'wp:term'?: {
      id: number;
      name: string;
      slug: string;
      taxonomy: string;
    }[][];
  };
};

export const TOTAL_POSTS = gql`
  query TotalPosts(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $where: RootQueryToPostConnectionWhereArgs
  ) {
    posts(
      after: $after
      before: $before
      first: $first
      last: $last
      where: $where
    ) {
      pageInfo {
        total
      }
    }
  }
`;

interface FetchPostsParams {
  locale: string;
  page: number;
  categoryId?: string;
  authorId?: number;
}

export const fetchPosts = async ({
  locale,
  page,
  categoryId,
  authorId,
}: FetchPostsParams) => {
  let fetchError;
  let posts;
  try {
    const localeQuery = locale ? `&lang=${locale}` : '';
    const categoryQuery = categoryId ? `&categories=${categoryId}` : '';
    const authorQuery = authorId ? `&author=${authorId}` : '';
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_HOST}/wp-json/wp/v2/posts?per_page=${NB_POSTS_PER_PAGE}&page=${page}${localeQuery}${categoryQuery}${authorQuery}&_embed`
    );
    posts = (await response.json()) as RestApiPost[];
  } catch (e) {
    fetchError = e;
  }

  return {
    posts,
    fetchError,
  };
};

export const mapRestApiPostToMappedPosts = (
  posts: RestApiPost[] | undefined
): Array<MappedPost> | undefined => {
  if (!posts || !Array.isArray(posts)) {
    return [];
  }

  return posts.map((post) => {
    const category = post._embedded['wp:term']?.find((item) =>
      item.find((term) => term.taxonomy === 'category')
    )?.[0];
    const image =
      post._embedded['wp:featuredmedia']?.[0]?.source_url ||
      (TemporaryCover as unknown as string);
    const isVideoPost = post?.format === 'video';
    const accentColor = post.ACF.accent_color || '#000000';
    const postVideo = post.ACF.video_id;
    const video = isVideoPost && postVideo ? postVideo : '';

    return {
      id: post?.id,
      title: post.title.rendered || '',
      slug: post.slug || '',
      categoryName: category?.name || '',
      categorySlug: category?.slug || '',
      accentColor,
      image,
      video,
    };
  });
};

export const getPageData = async ({
  params,
  locale,
}: GetStaticPropsContext<PagePageParams>) => {
  // Create new apollo client instead of calling `initializeApolloClient` which returns a shared one.
  // We do that to avoid caching all posts data in apollo cache, which is then sent to client
  const apolloClient = createApolloClient();

  if (!params?.page) {
    throw new Error('Page number is required.');
  }

  if (locale === undefined) {
    throw new Error('Locale is required.');
  }

  const page = parseInt(params.page, 10);

  const queryPromise = apolloClient.query<
    TotalPostsQuery,
    TotalPostsQueryVariables
  >({
    query: TOTAL_POSTS,
    errorPolicy: 'all',
    variables: {
      where: {
        wpmlLanguage: locale,
      },
    },
  });

  const fetchPromise = fetchPosts({ locale, page });

  const [{ data, error: queryError }, { posts, fetchError }] =
    await Promise.all([queryPromise, fetchPromise]);

  const mappedPosts = mapRestApiPostToMappedPosts(posts);

  return {
    totalAmountOfPosts: data?.posts?.pageInfo?.total,
    page: params.page,
    // Do not remove "null". getStaticProps will not serialise undefined
    error: fetchError || queryError || null,
    posts: mappedPosts || [],
  };
};

export const getCategoryPostsData = async ({
  params,
  locale,
}: GetStaticPropsContext<CategoryPostsPageParams>) => {
  // Create new apollo client instead of calling `initializeApolloClient` which returns a shared one.
  // We do that to avoid caching all posts data in apollo cache, which is then sent to client
  const apolloClient = createApolloClient();

  if (!params?.page) {
    throw new Error('Page number is required.');
  }
  if (!params?.categoryId || !params?.category) {
    throw new Error('Category is required.');
  }

  if (locale === undefined) {
    throw new Error('Locale is required.');
  }

  const page = parseInt(params.page, 10);

  const queryPromise = apolloClient.query<
    TotalPostsQuery,
    TotalPostsQueryVariables
  >({
    query: TOTAL_POSTS,
    errorPolicy: 'all',
    variables: {
      where: {
        categoryId: parseInt(params.categoryId, 10),
        wpmlLanguage: locale,
      },
    },
  });

  const fetchPromise = fetchPosts({
    locale,
    page,
    categoryId: params.categoryId,
  });

  const [{ data, error: queryError }, { posts, fetchError }] =
    await Promise.all([queryPromise, fetchPromise]);

  const mappedPosts = mapRestApiPostToMappedPosts(posts);

  return {
    totalAmountOfPosts: data?.posts?.pageInfo?.total,
    page: params.page,
    // Do not remove "null". getStaticProps will not serialise undefined
    error: fetchError || queryError || null,
    posts: mappedPosts || [],
  };
};

export async function getAllPostsSlugs(): Promise<string[]> {
  const apolloClient = initializeApollo();

  let paths: string[] = [];

  const { data } = await apolloClient.query<
    GetAllPostsSlugsQuery,
    GetAllPostsSlugsQueryVariables
  >({
    query: GET_ALL_POSTS_SLUGS,
    variables: {
      first: 500,
    },
  });

  const newPaths: string[] =
    data.posts?.nodes?.reduce<string[]>((acc, item) => {
      const locale = getLocalePath(item?.locale?.locale);
      const slug = item?.slug;

      if (slug && !findRedirectPath(slug, locale)) {
        acc.push(`/${locale}${slug}`);
      }

      return acc;
    }, []) || [];

  paths.push(...newPaths);

  return paths;
}
